import type { Matter, MatterDocumentSubItem } from "@/lib/types";

export type MatterSubItemScope = MatterDocumentSubItem;

const scopeMatterCollectionsToSubItem = (
  matter: Matter,
  subItem?: MatterSubItemScope,
): Matter => {
  if (!subItem) return matter;

  const selected = <T extends { id: string }>(items: T[]) =>
    items.filter((item) => item.id === subItem.id);

  switch (subItem.type) {
    case "expense":
      return { ...matter, expenses: selected(matter.expenses) };
    case "incident-report":
      return { ...matter, incidentReports: selected(matter.incidentReports) };
    case "insurance":
      return { ...matter, insurances: selected(matter.insurances) };
    case "lien":
      return { ...matter, liens: selected(matter.liens) };
    case "lost-wage":
      return { ...matter, lostWages: selected(matter.lostWages) };
    case "medical-treatment":
      return {
        ...matter,
        medicalTreatments: selected(matter.medicalTreatments),
        medicalTreatmentAppointments:
          matter.medicalTreatmentAppointments.filter(
            (item) => item.medicalTreatmentId === subItem.id,
          ),
        medicalTreatmentBalanceConfirmations:
          matter.medicalTreatmentBalanceConfirmations.filter(
            (item) => item.medicalTreatmentId === subItem.id,
          ),
        medicalTreatmentBills: matter.medicalTreatmentBills.filter(
          (item) => item.medicalTreatmentId === subItem.id,
        ),
        medicalTreatmentPlans: matter.medicalTreatmentPlans.filter(
          (item) => item.medicalTreatmentId === subItem.id,
        ),
        medicalTreatmentRecords: matter.medicalTreatmentRecords.filter(
          (item) => item.medicalTreatmentId === subItem.id,
        ),
      };
    case "medical-treatment-appointment":
      return {
        ...matter,
        medicalTreatmentAppointments: selected(
          matter.medicalTreatmentAppointments,
        ),
      };
    case "medical-treatment-balance-confirmation":
      return {
        ...matter,
        medicalTreatmentBalanceConfirmations: selected(
          matter.medicalTreatmentBalanceConfirmations,
        ),
      };
    case "medical-treatment-bill":
      return {
        ...matter,
        medicalTreatmentBills: selected(matter.medicalTreatmentBills),
      };
    case "medical-treatment-plan":
      return {
        ...matter,
        medicalTreatmentPlans: selected(matter.medicalTreatmentPlans),
      };
    case "medical-treatment-record":
      return {
        ...matter,
        medicalTreatmentRecords: selected(matter.medicalTreatmentRecords),
      };
    case "property-damage":
      return { ...matter, propertyDamages: selected(matter.propertyDamages) };
    case "settlement-counter":
      return {
        ...matter,
        settlementCounters: selected(matter.settlementCounters),
      };
    case "settlement-demand":
      return {
        ...matter,
        settlementDemands: selected(matter.settlementDemands),
      };
    case "settlement-offer":
      return {
        ...matter,
        settlementOffers: selected(matter.settlementOffers),
      };
    case "signature-request":
      return {
        ...matter,
        signatureRequests: matter.signatureRequests.filter(
          (item) => item.matterSignatureRequestId === subItem.id,
        ),
      };
    case "trust-check":
      return {
        ...matter,
        trustChecks: selected(matter.trustChecks),
        trustCheckConnections: matter.trustCheckConnections.filter(
          (item) => item.trustCheckId === subItem.id,
        ),
      };
    case "vehicle":
      return { ...matter, vehicles: selected(matter.vehicles) };
    default:
      return matter;
  }
};

export const scopeMatterToSubItem = (
  matter: Matter,
  subItem?: MatterSubItemScope,
): Matter => {
  if (!subItem) return matter;

  const medicalChildren = {
    "medical-treatment-appointment": matter.medicalTreatmentAppointments,
    "medical-treatment-balance-confirmation":
      matter.medicalTreatmentBalanceConfirmations,
    "medical-treatment-bill": matter.medicalTreatmentBills,
    "medical-treatment-plan": matter.medicalTreatmentPlans,
    "medical-treatment-record": matter.medicalTreatmentRecords,
  };
  const medicalChild =
    subItem.type in medicalChildren
      ? medicalChildren[subItem.type as keyof typeof medicalChildren].find(
          (item) => item.id === subItem.id,
        )
      : undefined;
  const parentScopedMatter = medicalChild?.medicalTreatmentId
    ? scopeMatterCollectionsToSubItem(matter, {
        type: "medical-treatment",
        id: medicalChild.medicalTreatmentId,
      })
    : matter;
  const scopedMatter = scopeMatterCollectionsToSubItem(
    parentScopedMatter,
    subItem,
  );

  // Retain matter-level contacts while excluding connections to other items in
  // the scoped collections. Medical children also inherit their parent's scope.
  return scopedMatter;
};
